import React, { useEffect, useState } from 'react';
import { Cookie, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'cookie-consent';
const CONSENT_EVENT = 'cookie-consent-changed';

export type ConsentValue = 'accepted' | 'rejected';

export const getCookieConsent = (): ConsentValue | null => {
  if (typeof window === 'undefined') return null;
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'accepted' || v === 'rejected' ? v : null;
};

export const setCookieConsent = (value: ConsentValue) => {
  localStorage.setItem(STORAGE_KEY, value);
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
};

export const onCookieConsentChange = (cb: (v: ConsentValue) => void) => {
  const handler = (e: Event) => cb((e as CustomEvent<ConsentValue>).detail);
  window.addEventListener(CONSENT_EVENT, handler);
  return () => window.removeEventListener(CONSENT_EVENT, handler);
};

const CookieConsentBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!getCookieConsent()) setVisible(true);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  const handle = (value: ConsentValue) => {
    setCookieConsent(value);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-4 animate-in slide-in-from-bottom-4 duration-500"
    >
      <div className="mx-auto max-w-5xl rounded-2xl border border-border bg-card/95 backdrop-blur-md shadow-2xl">
        <div className="flex flex-col gap-4 p-4 sm:p-5 sm:flex-row sm:items-center sm:gap-6">
          <div className="flex items-start gap-3 flex-1">
            <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Cookie className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display text-sm font-bold text-foreground flex items-center gap-2">
                <Cookie className="h-4 w-4 text-primary sm:hidden" />
                We value your privacy
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1 leading-relaxed">
                We use strictly necessary cookies to make this site work. With your consent we also use
                analytics cookies to understand how you use our site and improve it. See our{' '}
                <Link to="/cookies" className="underline hover:text-primary">Cookie Policy</Link>.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handle('rejected')}
              className="rounded-lg flex-1 sm:flex-initial"
            >
              Reject
            </Button>
            <Button
              size="sm"
              onClick={() => handle('accepted')}
              className="rounded-lg flex-1 sm:flex-initial font-semibold"
            >
              Accept
            </Button>
            <button
              type="button"
              onClick={() => handle('rejected')}
              aria-label="Close"
              className="hidden sm:flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
